import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import {
  Quote as QuoteIcon,
  Compass,
  Lightbulb,
  Sparkles,
  FlaskConical,
  ArrowRight,
  Trophy,
  TrendingUp,
  Heart,
  CalendarCheck,
  Star,
  Eye,
  BookOpen,
  Target,
  RotateCcw,
  Users2,
  PenLine,
  MessageSquarePlus,
  CheckSquare,
  Bookmark,
  Send,
  Check,
  Loader2,
  BarChart3,
  GitBranch,
  CalendarRange,
  ThumbsUp,
  ThumbsDown,
  FlaskRound,
  Info,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getDailyGradient } from "../../lib/homeContent";
import { trackHomeEvent } from "../../lib/homeAnalytics";
import { cardMotion, cardMotionLarge, prefersReduced, scrollReveal } from "./motionConstants";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import CaptureSheet from "../capture/CaptureSheet";
import { Skeleton } from "../ui/skeleton";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
import { setMessageReaction } from "../../api/companionMessages";
import { setInsightReaction } from "../../api/proactiveInsights";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useSaveTakeaway } from "../../hooks/useSaveTakeaway";
import CompanionMessageCTAs from "../shared/CompanionMessageCTAs";
import ReflectionCard from "./ReflectionCard";
import {
  useGrowthFeed,
  type FeedItem,
  type QuoteFeedItem,
  type CompanionFeedItem,
  type WisdomFeedItem,
  type EngagementReflectionFeedItem,
  type ProactiveInsightFeedItem,
  type ScienceFeedItem,
  type TipFeedItem,
  type PhilosophyFeedItem,
  type CtaFeedItem,
} from "../../hooks/useGrowthFeed";

// ─── Layout Builder ──────────────────────────────────────────────────────────
// Groups feed items into rows: compact cards (science, tip, philosophy) are
// paired side-by-side; richer cards (quote, companion, AI) span full width.

interface LayoutRow {
  id: string;
  type: "full" | "pair";
  items: FeedItem[];
}

function isCompactCard(type: FeedItem["type"]): boolean {
  return type === "science" || type === "tip" || type === "philosophy";
}

function buildLayoutRows(items: FeedItem[]): LayoutRow[] {
  const rows: LayoutRow[] = [];
  let i = 0;

  while (i < items.length) {
    const current = items[i];

    if (isCompactCard(current.type)) {
      const next = items[i + 1];
      if (next && isCompactCard(next.type)) {
        rows.push({
          id: `pair-${current.id}-${next.id}`,
          type: "pair",
          items: [current, next],
        });
        i += 2;
        continue;
      }
    }

    rows.push({ id: current.id, type: "full", items: [current] });
    i++;
  }

  return rows;
}

// ─── Animation ───────────────────────────────────────────────────────────────

const rowVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
};

// ─── Message Type Icons ──────────────────────────────────────────────────────

const MESSAGE_ICONS: Record<string, React.ElementType> = {
  welcome: Sparkles,
  check_in: Compass,
  milestone: Trophy,
  nudge: TrendingUp,
  insight_reflection: Lightbulb,
  pattern_observation: Lightbulb,
  encouragement: Heart,
  weekly_summary: CalendarCheck,
  trait_spotlight: Star,
  activity_suggestion: Compass,
};

const PHILOSOPHY_ICONS: Record<string, React.ElementType> = {
  eye: Eye,
  book: BookOpen,
  target: Target,
  rotate: RotateCcw,
  sparkles: Sparkles,
};

// ─── Accent colors ──────────────────────────────────────────────────────────

const QUOTE_ACCENT = [
  { mark: "text-violet-300", dash: "bg-violet-400" },
  { mark: "text-primary/60", dash: "bg-primary/60" },
  { mark: "text-emerald-300", dash: "bg-emerald-400" },
  { mark: "text-amber-300", dash: "bg-amber-400" },
  { mark: "text-rose-300", dash: "bg-rose-400" },
  { mark: "text-indigo-300", dash: "bg-indigo-400" },
  { mark: "text-teal-300", dash: "bg-teal-400" },
];

// ─── ReflectFooter ───────────────────────────────────────────────────────────
// Reusable inline reflect footer: button → expands textarea → submit → collapse

function ReflectFooter({
  buttonLabel,
  ButtonIcon,
  saveType,
  contentPrefix,
  buttonClassName,
  showBothTypes,
}: {
  buttonLabel: string;
  ButtonIcon: React.ElementType;
  saveType: "insight" | "action";
  contentPrefix?: string;
  buttonClassName?: string;
  showBothTypes?: boolean;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTakeaway = useSaveTakeaway();

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = async (type: "insight" | "action") => {
    const trimmed = text.trim();
    if (!trimmed || isSaving) return;

    setIsSaving(true);
    const content = contentPrefix
      ? `${contentPrefix} ${trimmed}`
      : trimmed;

    await saveTakeaway(type, content, {
      onSuccess: () => {
        setText("");
        setIsSaving(false);
        setShowSaved(true);
        setTimeout(() => {
          setShowSaved(false);
          setIsOpen(false);
        }, 1200);
      },
      onError: () => {
        setIsSaving(false);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !showBothTypes) {
      e.preventDefault();
      handleSave(saveType);
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setText("");
    }
  };

  if (showSaved) {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
        <Check className="w-3.5 h-3.5" />
        {t("saved", "Saved!")}
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="mt-3">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-auto px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground gap-1.5",
            buttonClassName
          )}
          onClick={() => setIsOpen(true)}
        >
          <ButtonIcon className="w-3.5 h-3.5" />
          {buttonLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t("type_your_thoughts")}
        aria-label={t("type_your_thoughts")}
        rows={2}
        className="w-full text-sm rounded-lg border border-border/60 bg-background/50 px-3 py-2 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            setIsOpen(false);
            setText("");
          }}
        >
          {t("cancel", "Cancel")}
        </Button>
        {showBothTypes ? (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-3 text-xs gap-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                  disabled={!text.trim() || isSaving}
                  onClick={() => handleSave("insight")}
                >
                  {isSaving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Lightbulb className="w-3 h-3" />
                  )}
                  {t("save_as_insight")}
                  <Info className="w-2.5 h-2.5 text-amber-400" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("insight_tooltip")}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-3 text-xs gap-1 border-primary/20 text-primary hover:bg-primary/5"
                  disabled={!text.trim() || isSaving}
                  onClick={() => handleSave("action")}
                >
                  {isSaving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CheckSquare className="w-3 h-3" />
                  )}
                  {t("save_as_action")}
                  <Info className="w-2.5 h-2.5 text-primary/40" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("action_tooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            size="sm"
            className="h-7 px-3 text-xs gap-1"
            disabled={!text.trim() || isSaving}
            onClick={() => handleSave(saveType)}
          >
            {isSaving ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            {t("save", "Save")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── OneClickAction ──────────────────────────────────────────────────────────
// A single button that saves a takeaway on click with feedback animation

function OneClickAction({
  label,
  Icon,
  saveType,
  content,
  className,
  onSaved,
}: {
  label: string;
  Icon: React.ElementType;
  saveType: "insight" | "action";
  content: string;
  className?: string;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const saveTakeaway = useSaveTakeaway();

  const handleClick = async () => {
    if (isSaving || showSaved) return;
    setIsSaving(true);
    await saveTakeaway(saveType, content, {
      onSuccess: () => {
        setIsSaving(false);
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
        onSaved?.();
      },
      onError: () => {
        setIsSaving(false);
      },
    });
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "h-auto px-2 py-1.5 text-xs gap-1.5",
        showSaved
          ? "text-emerald-600"
          : "text-muted-foreground hover:text-foreground",
        className
      )}
      disabled={isSaving}
      onClick={handleClick}
    >
      {isSaving ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : showSaved ? (
        <Check className="w-3.5 h-3.5" />
      ) : (
        <Icon className="w-3.5 h-3.5" />
      )}
      {showSaved ? t("saved", "Saved!") : label}
    </Button>
  );
}

// ─── ExperimentalBadge ───────────────────────────────────────────────────────

function ExperimentalBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60 font-medium">
      <FlaskRound className="w-3 h-3" />
      {t("ai_suggestion", "AI Suggestion")}
    </span>
  );
}

// ─── FeedbackButtons ────────────────────────────────────────────────────────

function FeedbackButtons({
  itemId,
  currentReaction,
  onReact,
}: {
  itemId: string;
  currentReaction?: "helpful" | "not_helpful" | null;
  onReact: (
    id: string,
    reaction: "helpful" | "not_helpful" | null,
    feedback?: string
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [reaction, setReaction] = useState<"helpful" | "not_helpful" | null>(
    currentReaction ?? null
  );
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleReaction = async (
    newReaction: "helpful" | "not_helpful"
  ) => {
    if (isSaving) return;
    const isSame = reaction === newReaction;
    const nextReaction = isSame ? null : newReaction;
    setReaction(nextReaction);

    if (nextReaction) {
      setShowFeedback(true);
    } else {
      setShowFeedback(false);
      setIsSaving(true);
      await onReact(itemId, null);
      setIsSaving(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!reaction || isSaving) return;
    setIsSaving(true);
    await onReact(itemId, reaction, feedback || undefined);
    setIsSaving(false);
    setShowFeedback(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (saved) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
        <Check className="w-3.5 h-3.5" />
        {t("feedback_submitted", "Thanks for your feedback")}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-9 w-9 p-0",
            reaction === "helpful"
              ? "text-emerald-600 bg-emerald-50"
              : "text-muted-foreground/50 hover:text-emerald-600"
          )}
          onClick={() => handleReaction("helpful")}
          aria-label={t("helpful", "Helpful")}
        >
          <ThumbsUp className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-9 w-9 p-0",
            reaction === "not_helpful"
              ? "text-red-500 bg-red-50"
              : "text-muted-foreground/50 hover:text-red-500"
          )}
          onClick={() => handleReaction("not_helpful")}
          aria-label={t("not_helpful", "Not helpful")}
        >
          <ThumbsDown className="w-4 h-4" />
        </Button>
      </div>
      {showFeedback && (
        <div className="space-y-1.5">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t(
              "feedback_tell_us_more",
              "Tell us more (optional)"
            )}
            rows={2}
            className="w-full text-xs rounded-lg border border-border/60 bg-background/50 px-3 py-2 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
          />
          <Button
            size="sm"
            className="h-7 px-3 text-xs"
            disabled={isSaving}
            onClick={handleSubmitFeedback}
          >
            {isSaving ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            {t("submit", "Submit")}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── QuickInputCard ──────────────────────────────────────────────────────────

export function QuickInputCard() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  // The text handed to the capture sheet; null = sheet closed.
  const [sheetText, setSheetText] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [hintSeen, setHintSeen] = useState(() => {
    try { return localStorage.getItem("capture_hint_seen") === "1"; } catch { return true; }
  });
  const dismissHint = () => {
    setHintSeen(true);
    try { localStorage.setItem("capture_hint_seen", "1"); } catch {}
  };

  // Auto-grow (start small, expand as user types)
  useEffect(() => {
    if (!textareaRef.current) return;
    if (!text) {
      textareaRef.current.style.height = "40px";
      return;
    }
    textareaRef.current.style.height = "0px";
    textareaRef.current.style.height = Math.min(Math.max(textareaRef.current.scrollHeight, 40), 200) + "px";
  }, [text]);

  const canCapture = text.trim().length >= 3;
  // Smart capture (spec 2026-11-02 §3): Done opens the sheet, which saves the
  // text as written and offers "Dive deeper?" afterwards.
  const openSheet = () => {
    if (canCapture) setSheetText(text.trim());
  };

  return (
    <motion.div
      {...cardMotion}
      className={cn(
        "relative rounded-2xl border border-primary/15",
        "bg-gradient-to-br from-card via-card to-primary/[0.02]",
        "p-5 transition-shadow duration-300",
        "shadow-sm shadow-primary/[0.03]",
        "hover:shadow-lg hover:shadow-primary/[0.06]",
        "focus-within:border-primary/20 focus-within:shadow-md focus-within:shadow-primary/[0.04]"
      )}
    >
      <label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-widest mb-2 block">
        {t("quick_capture")}
      </label>
      {!hintSeen && (
        <p className="text-[11.5px] text-muted-foreground/70 mt-1 flex items-start gap-1.5">
          <span className="flex-1">{t("home_capture_hint")}</span>
          <button onClick={dismissHint} aria-label={t("dismiss", "Dismiss")} className="text-muted-foreground/50 hover:text-muted-foreground">×</button>
        </p>
      )}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            openSheet();
          }
        }}
        placeholder={t("whats_on_your_mind")}
        aria-label={t("quick_capture")}
        rows={1}
        maxLength={5000}
        dir="auto"
        className="w-full text-sm bg-transparent placeholder:text-muted-foreground/50 focus:outline-none resize-none leading-relaxed text-start"
      />
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Button className="min-h-[44px] rounded-full px-5 gap-1.5" disabled={!canCapture} onClick={openSheet}>
          <Sparkles className="w-4 h-4" />
          {t("capture.done")}
        </Button>
      </div>
      <Dialog open={sheetText !== null} onOpenChange={(open) => { if (!open) setSheetText(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">{t("quick_capture")}</DialogTitle>
          {sheetText !== null && (
            <CaptureSheet
              text={sheetText}
              source="home"
              onSaved={(r) => {
                setText("");
                trackHomeEvent("quick_capture_submitted", { classified_as: r.items[0]?.kind ?? "insight" });
              }}
              onClose={() => setSheetText(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ─── Card Components ─────────────────────────────────────────────────────────

function QuoteCard({ item }: { item: QuoteFeedItem }) {
  const { t } = useTranslation();
  const accent = QUOTE_ACCENT[new Date().getDay()];
  const quoteText = t(item.content.text);

  return (
    <motion.div
      {...cardMotionLarge}
      className={cn(
        "relative rounded-2xl overflow-hidden bg-gradient-to-br",
        getDailyGradient(),
        "p-8 md:p-10 transition-shadow duration-300 hover:shadow-lg hover:shadow-violet-500/[0.06]"
      )}
    >
      <div className="absolute top-3 end-6 select-none pointer-events-none" aria-hidden="true">
        <QuoteIcon className="w-20 h-20 opacity-[0.08]" strokeWidth={1} />
      </div>
      <div className="relative space-y-4">
        <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-serif italic max-w-2xl">
          &ldquo;{quoteText}&rdquo;
        </p>
        <div className="flex items-center gap-3">
          <div className={cn("w-6 h-0.5 rounded-full", accent.dash)} />
          <p className="text-sm text-gray-500 font-medium tracking-wide">
            {t(item.content.author)}
          </p>
        </div>
      </div>
      <ReflectFooter
        buttonLabel={t("where_does_this_meet_you")}
        ButtonIcon={PenLine}
        saveType="insight"
        contentPrefix={`Re: "${quoteText}" \u2014`}
      />
    </motion.div>
  );
}

function CompanionCard({ item }: { item: CompanionFeedItem }) {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const msg = item.content;
  const Icon = MESSAGE_ICONS[msg.messageType] || Sparkles;

  return (
    <motion.div
      {...cardMotion}
      className="relative rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 p-5 transition-shadow duration-300 hover:shadow-lg hover:shadow-black/[0.04] hover:border-border"
    >
      <div
        className="absolute inset-y-3 start-0 w-1 rounded-full"
        style={{ backgroundColor: item.spaceColor }}
      />
      <div className="ps-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${item.spaceColor}15` }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: item.spaceColor }} />
          </div>
          <span
            className="text-xs font-semibold tracking-wide uppercase"
            style={{ color: item.spaceColor }}
          >
            {item.spaceName}
          </span>
          <span className="ms-auto flex items-center gap-2">
            <ExperimentalBadge />
            <span className="text-[11px] text-muted-foreground/50">
              {new Date(msg.createdOn).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </span>
          </span>
        </div>
        <div className="prose prose-sm prose-gray max-w-none [&_p]:my-1 [&_p]:leading-relaxed text-[14px] text-foreground/80">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {msg.content}
          </ReactMarkdown>
        </div>
        <CompanionMessageCTAs
          message={msg}
          spaceId={msg.fiSpace}
          className="mt-3"
        />
        <div className="mt-2">
          <FeedbackButtons
            itemId={msg._id}
            currentReaction={msg.userReaction}
            onReact={async (id, reaction, feedback) => {
              await setMessageReaction(retryFetch, id, reaction, feedback);
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

function EngagementReflectionCard({
  item,
}: {
  item: EngagementReflectionFeedItem;
}) {
  const { t } = useTranslation();

  return (
    <motion.div {...cardMotionLarge} className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50/90 via-orange-50/50 to-yellow-50/30 p-6 transition-shadow duration-300 hover:shadow-lg hover:shadow-amber-500/[0.05]">
      <div className="absolute -bottom-6 -end-6 w-28 h-28 rounded-full bg-amber-200/20 blur-2xl" />
      <div className="relative flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/70 shadow-sm flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-amber-500" />
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-amber-500">
              {t("home_feed_reflection")}
            </span>
            <ExperimentalBadge />
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            {item.content.summary}
          </p>
          {item.content.activityName && (
            <p className="text-xs text-amber-500/70 pt-1">
              {t("home_feed_from_activity", {
                name: item.content.activityName,
              })}
            </p>
          )}
          <ReflectFooter
            buttonLabel={t("what_will_you_do_next")}
            ButtonIcon={Target}
            saveType="action"
          />
        </div>
      </div>
    </motion.div>
  );
}

const INSIGHT_CARD_CONFIG: Record<
  string,
  {
    gradient: string;
    Icon: React.ElementType;
    iconColor: string;
    labelColor: string;
    labelKey: string;
  }
> = {
  progress_summary: {
    gradient: "from-primary/5/90 via-sky-50/60 to-cyan-50/40",
    Icon: BarChart3,
    iconColor: "text-primary",
    labelColor: "text-primary",
    labelKey: "home_feed_daily_summary",
  },
  pattern_detected: {
    gradient: "from-purple-50/90 via-violet-50/60 to-fuchsia-50/40",
    Icon: GitBranch,
    iconColor: "text-purple-500",
    labelColor: "text-purple-500",
    labelKey: "home_feed_cross_workflow_pattern",
  },
  weekly_summary: {
    gradient: "from-teal-50/90 via-emerald-50/60 to-cyan-50/40",
    Icon: CalendarCheck,
    iconColor: "text-teal-500",
    labelColor: "text-teal-500",
    labelKey: "weekly_summary",
  },
  monthly_summary: {
    gradient: "from-indigo-50/90 via-blue-50/60 to-violet-50/40",
    Icon: CalendarRange,
    iconColor: "text-indigo-500",
    labelColor: "text-indigo-500",
    labelKey: "monthly_summary",
  },
};

function ProactiveInsightCard({
  item,
}: {
  item: ProactiveInsightFeedItem;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const insight = item.content;
  const cfg = INSIGHT_CARD_CONFIG[insight.insightType] || INSIGHT_CARD_CONFIG.progress_summary;
  const { Icon } = cfg;

  // Show date for summary-type insights
  const isSummary = ["progress_summary", "weekly_summary", "monthly_summary"].includes(
    insight.insightType
  );
  const summaryDate = isSummary && insight.generatedAt
    ? new Date(insight.generatedAt).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <motion.div
      {...cardMotionLarge}
      className={cn(
        "relative rounded-2xl overflow-hidden bg-gradient-to-br p-6 transition-shadow duration-300 hover:shadow-lg hover:shadow-primary/[0.05]",
        cfg.gradient
      )}
    >
      <div className="absolute -top-8 -end-8 w-32 h-32 rounded-full blur-2xl" />
      <div className="relative flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/70 shadow-sm flex items-center justify-center">
          <Icon className={cn("w-5 h-5", cfg.iconColor)} />
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-[11px] font-bold uppercase tracking-widest",
                cfg.labelColor
              )}
            >
              {t(cfg.labelKey)}
            </span>
            <ExperimentalBadge />
          </div>
          {summaryDate && (
            <p className="text-xs text-muted-foreground/70">{summaryDate}</p>
          )}
          {insight.title && (
            <p className="text-sm font-medium text-gray-800">
              {insight.title}
            </p>
          )}
          <div className="prose prose-sm prose-gray max-w-none [&_p]:my-1 [&_p]:leading-relaxed text-[14px] text-foreground/80">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {insight.richContent || insight.message}
            </ReactMarkdown>
          </div>
          {insight.suggestedActions && insight.suggestedActions.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {insight.suggestedActions.map((action, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-xs gap-1"
                  onClick={() => {
                    if (action.actionUrl) navigate(action.actionUrl);
                  }}
                >
                  {action.label}
                  <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                </Button>
              ))}
            </div>
          )}
          <ReflectFooter
            buttonLabel={t("add_my_thoughts")}
            ButtonIcon={MessageSquarePlus}
            saveType="insight"
            contentPrefix={`Re: ${insight.title || t(cfg.labelKey)} \u2014`}
            showBothTypes
          />
          <div className="mt-2">
            <FeedbackButtons
              itemId={insight._id}
              currentReaction={insight.userReaction}
              onReact={async (id, reaction, feedback) => {
                await setInsightReaction(retryFetch, id, reaction, feedback);
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ScienceCard({
  item,
  onSaved,
}: {
  item: ScienceFeedItem;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const scienceText = t(item.content.text);

  return (
    <motion.div {...cardMotion} className="h-full rounded-2xl border border-emerald-100/80 bg-card/60 backdrop-blur-sm p-5 transition-shadow duration-300 hover:shadow-lg hover:shadow-emerald-500/[0.06]">
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
          <FlaskConical className="w-5 h-5 text-emerald-500" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-500 mb-1.5">
          {t("home_feed_science")}
        </span>
        <p className="text-sm text-gray-700 leading-relaxed flex-1">
          {scienceText}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-2">
          {t(item.content.source)}
        </p>
        <div className="mt-2">
          <OneClickAction
            label={t("save_as_insight")}
            Icon={Bookmark}
            saveType="insight"
            content={scienceText}
            className="hover:text-emerald-700"
            onSaved={onSaved}
          />
        </div>
      </div>
    </motion.div>
  );
}

function TipCard({ item }: { item: TipFeedItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div {...cardMotion} className="h-full rounded-2xl border border-sky-100/80 bg-card/60 backdrop-blur-sm p-5 transition-shadow duration-300 hover:shadow-lg hover:shadow-sky-500/[0.06]">
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mb-3">
          <Compass className="w-5 h-5 text-sky-500" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest text-sky-500 mb-1.5">
          {t("home_feed_tip")}
        </span>
        <p className="text-sm text-gray-700 leading-relaxed flex-1">
          {t(item.content.text)}
        </p>
        <Button
          variant="link"
          size="sm"
          className="px-0 h-auto mt-2 text-sky-500 hover:text-sky-600 justify-start group/btn"
          onClick={() => navigate(item.content.route)}
        >
          {t("home_feed_try_it")}
          <ArrowRight className="w-3.5 h-3.5 ms-1.5 rtl:rotate-180 transition-transform group-hover/btn:translate-x-0.5 rtl:group-hover/btn:-translate-x-0.5" />
        </Button>
      </div>
    </motion.div>
  );
}

// ─── WisdomCard ──────────────────────────────────────────────────────────────
// The single daily wisdom slot: science (cited) / tip / quote, rotated by day.
// Reuses the existing card bodies so each variant keeps its own styling.

function WisdomCard({ item }: { item: WisdomFeedItem }) {
  const c = item.content;
  if (c.wisdomType === "quote")
    return <QuoteCard item={{ id: "daily-quote", type: "quote", content: c.quote }} />;
  if (c.wisdomType === "science")
    return (
      <ScienceCard
        item={{ id: "daily-science", type: "science", content: c.science }}
        onSaved={() => trackHomeEvent("wisdom_saved", { wisdomType: "science" })}
      />
    );
  return <TipCard item={{ id: "daily-tip", type: "tip", content: c.tip }} />;
}

function PhilosophyCard({ item }: { item: PhilosophyFeedItem }) {
  const { t } = useTranslation();
  const Icon = PHILOSOPHY_ICONS[item.content.icon] || Sparkles;

  return (
    <motion.div {...cardMotion} className="h-full rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm p-5 transition-shadow duration-300 hover:shadow-lg hover:shadow-primary/[0.06]">
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/[0.06] flex items-center justify-center mb-3">
          <span className="text-sm font-bold text-primary/70">
            {item.content.number}
          </span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-3.5 h-3.5 text-primary/40" />
          <span className="text-sm font-semibold text-foreground">
            {t(item.content.titleKey)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">
          {t(item.content.descKey)}
        </p>
      </div>
    </motion.div>
  );
}

function CtaCard({ item }: { item: CtaFeedItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (item.content.ctaType === "create_space") {
    return (
      <motion.div
        {...cardMotionLarge}
        className={cn(
          "relative rounded-2xl overflow-hidden",
          "bg-gradient-to-br from-primary/[0.03] via-primary/[0.06] to-primary/[0.03]",
          "border border-primary/10 p-8 text-center",
          "transition-shadow duration-300 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/[0.04]"
        )}
      >
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Users2 className="w-6 h-6 text-primary/70" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1.5">
          {t("home_cta_create_space")}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto leading-relaxed">
          {t("home_cta_create_space_desc")}
        </p>
        <Button
          size="sm"
          className="rounded-xl px-5"
          onClick={() => navigate("/spaces")}
        >
          {t("home_cta_get_started")}
          <ArrowRight className="w-3.5 h-3.5 ms-1.5 rtl:rotate-180" />
        </Button>
      </motion.div>
    );
  }

  return null;
}

// ─── Feed Card Switch ────────────────────────────────────────────────────────

function FeedCard({ item }: { item: FeedItem }) {
  switch (item.type) {
    case "quote":
      return <QuoteCard item={item} />;
    case "companion":
      return <CompanionCard item={item} />;
    case "wisdom":
      return <WisdomCard item={item} />;
    case "engagement_reflection":
      return <EngagementReflectionCard item={item} />;
    case "proactive_insight":
      return <ProactiveInsightCard item={item} />;
    case "science":
      return <ScienceCard item={item} />;
    case "tip":
      return <TipCard item={item} />;
    case "philosophy":
      return <PhilosophyCard item={item} />;
    case "cta":
      return <CtaCard item={item} />;
    default:
      return null;
  }
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function FeedSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground/60 text-center py-2">
        {t("preparing_your_feed")}
      </p>
      <Skeleton className="h-36 rounded-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
      <Skeleton className="h-28 rounded-2xl" />
    </div>
  );
}

function FeedEmpty() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="text-center py-12 px-6">
      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <Sparkles className="w-6 h-6 text-primary/60" />
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1.5">
        {t("feed_empty_title")}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto leading-relaxed">
        {t("feed_empty_description")}
      </p>
      <Button
        size="sm"
        variant="outline"
        className="rounded-xl px-5"
        onClick={() => navigate("/marketplace")}
      >
        {t("browse_and_try")}
        <ArrowRight className="w-3.5 h-3.5 ms-1.5 rtl:rotate-180" />
      </Button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function GrowthFeed() {
  const { t } = useTranslation();
  const { items, isLoading, reflection } = useGrowthFeed();

  const rows = useMemo(() => buildLayoutRows(items), [items]);

  if (isLoading && items.length === 0) {
    return <FeedSkeleton />;
  }

  if (!isLoading && items.length === 0) {
    return <FeedEmpty />;
  }

  return (
    <div className="space-y-4">
      {reflection && (
        <ReflectionCard
          content={reflection.content}
          onAnswer={reflection.answer}
          onSkip={reflection.skip}
        />
      )}
      {rows.map((row, i) => (
        <motion.div
          key={row.id}
          custom={i}
          variants={prefersReduced ? reducedMotionVariants : rowVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {row.type === "pair" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FeedCard item={row.items[0]} />
              <FeedCard item={row.items[1]} />
            </div>
          ) : (
            <FeedCard item={row.items[0]} />
          )}
        </motion.div>
      ))}
    </div>
  );
}
